import Controller, { OnAfterNavigate, OnBeforeSubmit, OnChange, OnClick, OnMount, OnValidate, OnAfterFetch, OnBlur, OnBeforeFetch } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent, ValidateEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ActionAPI, CheckboxAPI, FormAPI, InputAPI, LocatorFieldAPI, RadioGroupAPI, SelectableItem, WizardAPI, AutocompleteAPI, FieldAPI, DateFieldAPI, LocatorAPI, TextAreaAPI, ToastService, FetchAPI } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import DialogService from '../../../../services/dialog-service';
import { Inject } from '@odinjs/odin';

import moment, { Moment } from 'moment';

import BaseController from '../../../BaseController';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { HealthHistoryMedicinesEditAliases as Alias, FragmentNames } from '../../../../codegen';
import { Endpoint } from '../../../../constants/endpoints';
import { WizardSteps } from '../../../../constants/wizards';
import { DateTypeId, DomainType, HealthHistoryMedicineResponse, HealthHistoryType, SearchType } from '../../health-history-types';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { YesNoType } from '../../../../utils/enum-utils';
import { DEFAULT_DATE_PATTERN, MONTH_YEAR_PATTERN, YEAR_PATTERN, ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { ApplicationRoute } from '../../../../constants/routes';
import { MedicinesRadioGroupId } from '../radio-group-data/radio-group-data-expressions';
import HealthHistoryCommons from '../../health-history-commons';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { FunctionName, PEP_281Parameters } from '../../../../utils/parameters';

@Controller({ component: Alias.LAYOUT_HEALTH_HISTORY_MEDICINES, fragment: FragmentNames.HEALTH_HISTORY_MEDICINES_EDIT })
export default class HealthHistoryMedicinesEditController extends BaseController {

  @Inject(Alias.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(Alias.FORM_ONE)
  private formOneAPI!: FormAPI;

  @Inject(Alias.FORM_TWO)
  private formTwoAPI!: FormAPI;

  @Inject(Alias.FORM_THREE)
  private formThreeAPI!: FormAPI;

  @Inject(Alias.AUTOCOMPLETE_DATE_TYPE)
  private autocompleteDateTypeAPI!: AutocompleteAPI;

  @Inject(Alias.FIELD_START_DATE)
  private fieldStartDateAPI!: FieldAPI;

  @Inject(Alias.FIELD_START_MONTH)
  private fieldStartMonthAPI!: FieldAPI;

  @Inject(Alias.FIELD_START_YEAR)
  private fieldStartYearAPI!: FieldAPI;

  @Inject(Alias.INPUT_START_YEAR)
  private inputStartYearAPI!: InputAPI;

  @Inject(Alias.INPUT_START_MONTH)
  private inputStartMonthAPI!: InputAPI;

  @Inject(Alias.DATE_FIELD_START_DATE)
  private dateFieldStartDateAPI!: DateFieldAPI;

  @Inject(Alias.INPUT_UNREGISTRED_MEDICINE)
  private inputUnregistredMedicineAPI!: InputAPI;

  @Inject(Alias.AUTOCOMPLETE_UNITY_MEASURE)
  private autocompleteUnityAPI!: AutocompleteAPI;

  @Inject(Alias.INPUT_QT_DOSE)
  private inputQtDoseAPI!: InputAPI;

  @Inject(Alias.AUTOCOMPLETE_CHANNEL_ADM)
  private autocompleteChannelAdmAPI!: AutocompleteAPI;

  @Inject(Alias.AUTOCOMPLETE_INTERVAL)
  private autocompleteIntervalAPI!: AutocompleteAPI;

  @Inject(Alias.FIELD_UNKNOWN_INFO_MEDICINE)
  private fieldUnknownInfoMedAPI!: FieldAPI;

  @Inject(Alias.LOCATOR_MEDICINE)
  private locatorMedicineAPI!: LocatorAPI;

  @Inject(Alias.INPUT_SCHEDULES)
  private inputSchedulesAPI!: InputAPI;

  @Inject(Alias.AUTOCOMPLETE_PROVENANCE)
  private autocompleteProvenanceAPI!: AutocompleteAPI;

  @Inject(Alias.AUTOCOMPLETE_INTERRUPT_REASON)
  private autocompleteInterruptReasonAPI!: AutocompleteAPI;

  @Inject(Alias.INPUT_MEDICINE)
  private inputMedicineAPI!: InputAPI;

  @Inject(Alias.DATE_FIELD_END_DATE)
  private datefieldEndDateAPI!: DateFieldAPI;

  @Inject(Alias.DATE_FIELD_LAST_DATE)
  private datefieldLastDateAPI!: DateFieldAPI;

  @Inject(Alias.TEXT_AREA_REACTION)
  private textAreaReactionAPI!: TextAreaAPI;

  @Inject(Alias.CHECKBOX_SELF_MEDICATION)
  private checkboxSelfMedAPI!: CheckboxAPI;

  @Inject(Alias.CHECKBOX_UNKNOWN_START)
  private checkboxUnknownStartAPI!: CheckboxAPI;

  @Inject(Alias.CHECKBOX_UNKNOWN_END)
  private checkboxUnknownEndAPI!: CheckboxAPI;

  @Inject(Alias.CHECKBOX_UNKNOWN_LAST_DOSE)
  private checkboxUnknownLastDoseAPI!: CheckboxAPI;

  @Inject(Alias.FIELD_FIRST_TRIMESTER)
  private fieldFirstTrimesterAPI!: FieldAPI;

  @Inject(Alias.FIELD_SECOND_TRIMESTER)
  private fieldSecondTrimesterAPI!: FieldAPI;

  @Inject(Alias.FIELD_THIRD_TRIMESTER)
  private fieldThirdTrimesterAPI!: FieldAPI;

  @Inject(Alias.FIELD_JUSTIFICATION_FOR_END)
  private fieldJustificationForEndAPI!: FieldAPI;

  @Inject(Alias.ACTION_DELETE_HEALTH_HISTORY_MEDICINES)
  private actionDeleteAPI!: ActionAPI;

  @Inject(Alias.ACTION_NEW_HEALTH_HISTORY_MEDICINES)
  private actionNewAPI!: ActionAPI;

  @Inject(Alias.ACTION_RELEASE_HEALTH_HISTORY_MEDICINES)
  private actionReleaseAPI!: ActionAPI;

  @Inject(Alias.FETCH_UNITY_MEASURE)
  private fetchUnityMeasureAPI!: FetchAPI;

  @Inject(Alias.FETCH_CHANNEL_ADM)
  private fetchChannelAdmAPI!: FetchAPI;

  @OnMount(Alias.WIZARD)
  handleMountWizard(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
    InformationBarUtils.collapsedBar = true;
    HealthHistoryListContext.currentTab = HealthHistoryType.MEDICINES;
    this.getInfoPatient();
    this.handleFetchMedicine();
  }

  @OnMount(Alias.FORM_ONE)
  handleMountFormOne(event: MountEvent): void {
    const api = event.target as FormAPI;
    api.setData({...api.getData(), alert: true });
    applicationContext.popNavigationItem();
    this.formOneAPI.setLoading(true);
    this.handleJustificationForEnd();
    this.actionDeleteAPI.setEnabled(false);
    this.actionNewAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);
  }

  @OnMount(Alias.RADIO_GROUP_CONDITION)
  handleMountRadiogroup(event: MountEvent): void {
    const api = event.target as RadioGroupAPI;
    this.setRadioGroupValues(MedicinesRadioGroupId.CONTINUOUS_MEDICATION, api);
  }

  @OnAfterNavigate(Alias.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.handleStepDone(true, WizardSteps.STEP_THREE);
    this.handleStepDone(true, WizardSteps.STEP_TWO);
  }

  @OnValidate(Alias.FORM_ONE)
  handleValidateFormOne(validationFormOne: ValidateEvent): void {
    this.handleStepDone(validationFormOne.valid, WizardSteps.STEP_ONE);
  }

  @OnValidate(Alias.FORM_TWO)
  handleValidateFormTwo(validationFormTwo: ValidateEvent): void {
    this.handleStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO);
  }

  @OnValidate(Alias.FORM_THREE)
  handleValidateFormThree(validationFormThree: ValidateEvent): void {
    this.handleStepDone(validationFormThree.valid, WizardSteps.STEP_THREE);
  }

  @OnBeforeFetch(Alias.FETCH_UNITY_MEASURE)
  handleBeforeFetchUnityMeasure(event: FetchEvent): void {
    const fetchApi = event.target as FetchAPI;
    const medicineCode = this.context.get('medicineCode');
    const medicineDescription = this.context.get('medicineDescription');

    if (medicineCode) {
        fetchApi.setParameters({ medicineCode })
    }
    if (medicineDescription) {
        fetchApi.setParameters({ medicineDescription })
    }
  }

  @OnBeforeFetch(Alias.FETCH_CHANNEL_ADM)
  handleBeforeFetchChannelAdm(event: FetchEvent): void {
    const fetchApi = event.target as FetchAPI;
    const medicineCode = this.context.get('medicineCode');

    if (medicineCode) {
        fetchApi.setParameters({ medicineCode })
    }
  }

  @OnAfterFetch(Alias.FETCH_UNITY_MEASURE)
  handleFetchUnityMeasure(event: FetchEvent): void {
    this.mountUnityMeasureAfterFetch(event.data as Record<string, Value>[]);
  }

  @OnAfterFetch(Alias.FETCH_CHANNEL_ADM)
  handleFetchChannelAdm(event: FetchEvent): void {
    this.mountChannelAdmAfterFetch(event.data as Record<string, Value>[]);
  }

  @OnChange(Alias.AUTOCOMPLETE_DATE_TYPE)
  handleChangeDateType(event: ChangeEvent<DomainType>): void {
    const dateType = event.value;
    const oldDateType = event?.oldValue;
    this.handleDateTypeFields(dateType, oldDateType);
  }

  @OnChange(Alias.LOCATOR_MEDICINE)
  handleChangeLocator(event: ChangeEvent<LocatorFieldAPI>): void {
    const medicine = event.value as unknown as SearchType;
    this.context.set({ medicine: medicine?medicine:null })
    this.handleDisabledField();
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_INFO_MEDICINE)
  handleChangeUnknownInfo(event: ChangeEvent<CheckboxAPI>): void {
    const unknownInfoMedication = event.value as unknown as boolean;
    this.inputQtDoseAPI.setValue('');
    this.handleDisabledUnknown(unknownInfoMedication);
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_END)
  handleChangeUnknownEnd(event: ChangeEvent<CheckboxAPI>): void {
    const unknownEnd = event.value as unknown as boolean;
    this.datefieldEndDateAPI.setValue('');
    this.disablingDateFields(unknownEnd, this.datefieldEndDateAPI);
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_LAST_DOSE)
  handleChangeUnknownLastDose(event: ChangeEvent<CheckboxAPI>): void {
    const unknwonLastDose = event.value as unknown as boolean;
    this.datefieldLastDateAPI.setValue('');
    this.disablingDateFields(unknwonLastDose, this.datefieldLastDateAPI);
  }

  @OnChange(Alias.CHECKBOX_UNKNOWN_START)
  handleChangeUnknownStart(event: ChangeEvent<CheckboxAPI>): void {
    const unknownStart = event.value as unknown as boolean;
    this.enableUnknownStart(unknownStart);
  }

  @OnChange(Alias.RADIO_GROUP_CONDITION)
  handleChangeRadioGroup(event: ChangeEvent<RadioGroupAPI>): void {
    const value = event.value as unknown as SelectableItem;
    const deniesMedicines = value.key === YesNoType.YES;
    this.context.set({ deniesMedicines, medicineCode: null })
    this.context.set({ deniesMedicines })
    if (deniesMedicines) {
      this.handleDeniesEmptyFields();
      this.handleDisabledDenies(true);
      this.checkUnregistredMed();
      return;
    }
    this.handleDisabledDenies(deniesMedicines);
    this.checkUnregistredMed();
  }

  @OnChange(Alias.INPUT_UNREGISTRED_MEDICINE)
  handleInputUnregistredMed(event: ChangeEvent<InputAPI>): void {
    const value = event.value as unknown as string;
    const disableMedicine = (value !== '');
    if (disableMedicine) {
      this.disableMedicines(disableMedicine);
      return;
    }
    this.disableMedicines(disableMedicine);
  }

  @OnBlur(Alias.INPUT_UNREGISTRED_MEDICINE)
  handleBlurInputUnregisterdMed(): void {
    this.handleBlurUnregisterMedicine();
  }

  @OnClick(Alias.ACTION_RELEASE_HEALTH_HISTORY_MEDICINES)
  handleClickRelease(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idHealthHistory as number);
  }

  @OnClick(Alias.ACTION_NEW_HEALTH_HISTORY_MEDICINES)
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.HEALTH_HISTORY_MEDICINES_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick(Alias.ACTION_DELETE_HEALTH_HISTORY_MEDICINES)
  handleClickDelete(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.confirmDelete(idHealthHistory as number);
  }

  @OnBeforeSubmit(Alias.WIZARD)
  handleBeforeSubmit(): void {
    const { encounterId, patientId, idHealthHistory } = this.historyService.getCurrentRouteParams();
    const user                       = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const formDataOne                = this.formOneAPI.getRecord();
    const formDataTwo                = this.formTwoAPI.getRecord();
    const formDataThree              = this.formThreeAPI.getRecord();
    const isFieldStartDateVisible    = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible   = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible    = this.fieldStartYearAPI.isVisible();
    const medicines = this.context.get('medicines') as HealthHistoryMedicineResponse;
    const body = {
      administrationChannel: (formDataTwo.administrationChannel as DomainType)?.valueDomain,
      alertDescription: BooleanUtils.isPositiveValue(formDataOne.alert as boolean),
      beginningDateCode: (formDataThree.beginningDate as DomainType)?.valueDomain,
      continuousMedication: (formDataOne.continuousMedication as SelectableItem)?.key,
      dateLastDose: (formDataThree.dateLastDose ? moment(String(formDataThree.dateLastDose)).utc().format(ISO_FULL_DATE_FORMAT) : undefined),
      endDate: (formDataThree.dateEnd ? moment(String(formDataThree.dateEnd)).utc().format(ISO_FULL_DATE_FORMAT) : undefined),
      firstTrimester: BooleanUtils.isPositiveValue(formDataThree.firstTrimester as boolean),
      idHealthHistory: idHealthHistory as number,
      interruptReasonCode: (formDataTwo.interruptReason as DomainType)?.valueDomain,
      intervalCode: (formDataTwo.interval as DomainType)?.valueDomain,
      justificationForEnd: (formDataThree.justificationForEnd as DomainType)?.valueDomain,
      medicationDescriptionCode: (formDataOne.medicationDescription as SearchType)?.id,
      numberService: parseInt(encounterId as unknown as string),
      observation: formDataThree.observation,
      physicalPerson: patientId,
      professionalCode: user.physicalPersonId,
      provenanceCode: (formDataTwo.provenance as DomainType)?.valueDomain,
      qtDose: formDataOne.qtDose as number,
      reaction: formDataThree.reaction,
      recordDate: medicines.recordDate,
      schedules: formDataTwo.schedules,
      secondTrimester: BooleanUtils.isPositiveValue(formDataThree.secondTrimester as boolean),
      securityLevelCode: (formDataTwo.securityLevel as SearchType)?.id,
      selfMedication: BooleanUtils.isPositiveValue(formDataThree.selfMedication as boolean),
      startDate: (isFieldStartDateVisible && formDataThree.dateStart ? moment(String(formDataThree.dateStart)).utc().format(ISO_FULL_DATE_FORMAT) : undefined),
      startMonth: (isFieldStartMonthVisible && formDataThree.startMonth) ? moment(String(formDataThree.startMonth)).startOf('month').utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      startYear: (isFieldStartYearVisible && formDataThree.startYear) ? HealthHistoryCommons.getOnlyYear(formDataThree.startYear) : undefined,
      thirdTrimester: BooleanUtils.isPositiveValue(formDataThree.thirdTrimester as boolean),
      unityMeasurementCode: (formDataTwo.unityMeasurement as DomainType)?.valueDomain,
      unknownEnd: BooleanUtils.isPositiveValue(formDataThree.unknownEnd as boolean),
      unknownInfoMedication: BooleanUtils.isPositiveValue(formDataOne.unknownInfoMedication as boolean),
      unknownLastDose: BooleanUtils.isPositiveValue(formDataThree.unknownLastDose as boolean),
      unknownStart: BooleanUtils.isPositiveValue(formDataThree.unknownStart as boolean),
      unregisteredMedication: formDataOne.unregisteredMedication,
    } as Record<string, Value>;
    this.handleCreateMedicineInUse(body);
  }

  private getInfoPatient(): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PATIENT_GENDER, {encounterId})
      .then((res) => {
        const data = res.body as Record<string, Value>;
        const genderFemale = 'F';
        this.displayPregnancyChecks(data?.gender === genderFemale);
      }).catch((err) => err?.body?.errorMessage && this.showToast(err.body.errorMessage, ToastLevel.ERROR));
  }

  private displayPregnancyChecks(check: boolean): void {
    this.fieldFirstTrimesterAPI.setVisible(check);
    this.fieldSecondTrimesterAPI.setVisible(check);
    this.fieldThirdTrimesterAPI.setVisible(check);
  }

  public handleJustificationForEnd() {
    const justificationForEnd = FoundationUtils.findModuleParam(FunctionName.PEP_281, PEP_281Parameters.JUSTIFICATION_FOR_END)
    HealthHistoryCommons.setVisibleField(this.fieldJustificationForEndAPI, BooleanUtils.isTrueValue(justificationForEnd?.toString()));
  }

  private handleFetchMedicine(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_HEALTH_HISTORY_MEDICINE, { sequenceNumberMedicine: idHealthHistory })
      .then((res) => {
        const record = res.body as HealthHistoryMedicineResponse;
        this.handleTreatData(record);
        this.handleStepDone(true, WizardSteps.STEP_ONE);
        this.handleStepDone(true, WizardSteps.STEP_TWO);
        this.handleStepDone(true, WizardSteps.STEP_THREE);
      }).catch((err) => err?.body?.errorMessage && this.showToast(err.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formOneAPI.setLoading(false);
        this.actionNewAPI.setEnabled(true);
      })
  }

  private handleTreatData(record: HealthHistoryMedicineResponse): void {
    const alert                   = BooleanUtils.isTrueValue(record.alertDescription);
    const firstTrimester          = BooleanUtils.isTrueValue(record.firstTrimester);
    const secondTrimester         = BooleanUtils.isTrueValue(record.secondTrimester);
    const selfMedication          = BooleanUtils.isTrueValue(record.selfMedication);
    const thirdTrimester          = BooleanUtils.isTrueValue(record.thirdTrimester);
    const unknownEnd              = BooleanUtils.isTrueValue(record.unknownEnd);
    const unknownInfoMedication   = BooleanUtils.isTrueValue(record.unknownInfoMedication);
    const unknownLastDose         = BooleanUtils.isTrueValue(record.unknownLastDose);
    const unknownStart            = BooleanUtils.isTrueValue(record.unknownStart);

    const dateStart               = record.startDate ?  moment(String(record.startDate)).utc().format(DEFAULT_DATE_PATTERN) : undefined;
    const startMonth              = record.startMonth ? moment(String(record.startMonth)).utc().format(MONTH_YEAR_PATTERN) : undefined;
    const dateEnd                 = record.endDate ? moment(record.endDate).utc().format(DEFAULT_DATE_PATTERN) : undefined;
    const dateLastDose            = record.dateLastDose ? moment(record.dateLastDose).utc().format(DEFAULT_DATE_PATTERN) : undefined;

    const radioData               = record.continuousMedication;
    const continuousMedication    = { key: radioData?.id, label: '', value: radioData?.id }

    const dateType                = record.beginningDate as DomainType;

    const medicine                = record.medicationDescription as SearchType;
    const deniesMedicines         = radioData.id === YesNoType.YES;
    const medicineCode            = deniesMedicines ? undefined : record.medicationDescription?.id as unknown as string;
    const medicationDescription   = record.unregisteredMedication ? '' : record.medicationDescription as SearchType;
    const medicineDescription     = record.unregisteredMedication && !deniesMedicines ? record.unregisteredMedication : null;

    const medicinesRecord = {
      ...record,
      alert,
      continuousMedication,
      dateEnd,
      dateLastDose,
      dateStart,
      firstTrimester,
      medicationDescription,
      secondTrimester,
      selfMedication,
      startMonth,
      thirdTrimester,
      unknownEnd,
      unknownInfoMedication,
      unknownLastDose,
      unknownStart,
    };

    this.context.set({
      deniesMedicines,
      initialDateType: dateType,
      medicine,
      medicineCode,
      medicineDescription,
      medicines: medicinesRecord
    });

    this.handleValidations(record);
  }

  private handleVisibilityDates(dateType: DomainType | Record<string, Value>): void {
      const isDayMonthYear = dateType?.valueDomain === DateTypeId.DAY_MONTH_YEAR;
      const isMonthYear    = dateType?.valueDomain === DateTypeId.MONTH_YEAR;
      const isYear         = dateType?.valueDomain === DateTypeId.YEAR;

      HealthHistoryCommons.setVisibleField(this.fieldStartDateAPI, isDayMonthYear || (!isDayMonthYear && !dateType?.valueDomain));
      HealthHistoryCommons.setVisibleField(this.fieldStartMonthAPI, isMonthYear);
      HealthHistoryCommons.setVisibleField(this.fieldStartYearAPI, isYear);
  }

  private handleValidations(record: HealthHistoryMedicineResponse): void {
    const medicinesRecord         = this.context.get('medicines');
    const deniesMedicines         = this.context.get('deniesMedicines');
    const dateType                = record.beginningDate as DomainType;
    const oldValueDate            = this.getOldValueDate(record);
    this.formOneAPI.setEditing(true);
    this.formOneAPI.validate();
    this.formOneAPI.setDisabled(true);

    this.autocompleteUnityAPI.setEnabled(this.isNotEmpty(record.unityMeasurement));
    this.autocompleteChannelAdmAPI.setEnabled(this.isNotEmpty(record.administrationChannel));

    this.actionDeleteAPI.setEnabled(true);
    this.actionReleaseAPI.setEnabled(true);

    this.formOneAPI.setData(medicinesRecord as unknown as Record<string, HealthHistoryMedicineResponse>);
    this.formTwoAPI.setData(medicinesRecord as unknown as Record<string, HealthHistoryMedicineResponse>);
    this.formThreeAPI.setData(medicinesRecord as unknown as Record<string, HealthHistoryMedicineResponse>);

    const disableMedicine = (record.unregisteredMedication && record.unregisteredMedication as string !== '');

    this.handleDateTypeFields(dateType, oldValueDate as unknown as DomainType);
    this.enableUnknownStart(BooleanUtils.isTrueValue(record.unknownStart));
    if (deniesMedicines) {
      this.handleDisabledDenies(true);
    }

    this.handleDisabledField();
    this.handleDisabledUnknown(BooleanUtils.isTrueValue(record.unknownInfoMedication));
    if (disableMedicine) {
      this.disableMedicines(disableMedicine);
    }
    if (!deniesMedicines) {
      this.disablingDateFields(BooleanUtils.isTrueValue(record.unknownEnd), this.datefieldEndDateAPI);
      this.disablingDateFields(BooleanUtils.isTrueValue(record.unknownLastDose), this.datefieldLastDateAPI);
    }
  }

  private getOldValueDate(record: HealthHistoryMedicineResponse) {
    return (record.startDate && moment(record.startDate).format(DEFAULT_DATE_PATTERN)) ||
        (record.startYear && moment(record.startYear).format(YEAR_PATTERN)) ||
        (record.startMonth && moment(record.startMonth).format(MONTH_YEAR_PATTERN));
  }

  private isNotEmpty(object?: DomainType): boolean {
    return !!object && Object.keys(object).length !== 0;
  }

  private handleDateTypeFields(dateType: DomainType, oldDateType: DomainType | undefined): void {
    const oldValue = oldDateType ? this.getValueByDateType(oldDateType) : '';

    this.handleVisibilityDates(dateType);

    if (oldValue) {
      this.setOldValueInput(oldValue, dateType);
    }
  }

  private getValueByDateType(dateType: DomainType): Moment | undefined {
    let dateValue;

    switch(dateType.valueDomain) {
      case DateTypeId.YEAR: {
        if (!this.inputStartYearAPI.getValue()) break;
        dateValue = moment(this.inputStartYearAPI.getValue());
        break;
      }
      case DateTypeId.MONTH_YEAR: {
        if (!this.inputStartMonthAPI.getValue()) break;
        dateValue = moment(this.inputStartMonthAPI.getValue() as string);
        break;
      }
      default: {
        if (!this.dateFieldStartDateAPI.getValue()) break;
        dateValue = moment(this.dateFieldStartDateAPI.getValue() as string);
        break;
      }
    }

    return dateValue;
  }

  private setOldValueInput(oldValue : Moment, dateType: DomainType): void  {
    const startYear   = 'year';
    const startMonth  = 'month';
    switch(dateType.valueDomain) {
      case DateTypeId.YEAR: {
        this.fieldStartYearAPI.setValue(moment(oldValue).startOf(startYear).format(YEAR_PATTERN));
        break;
      }
      case DateTypeId.MONTH_YEAR: {
        this.fieldStartMonthAPI.setValue(moment(oldValue).startOf(startMonth).format(MONTH_YEAR_PATTERN));
        break;
      }
      default: {
        this.fieldStartDateAPI.setValue(moment(oldValue).format(DEFAULT_DATE_PATTERN));
        break;
      }
    }
  }

  private enableUnknownStart(check: boolean): void {
    this.autocompleteDateTypeAPI.setEnabled(!check);
    const isFieldStartDateVisible   = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible  = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible   = this.fieldStartYearAPI.isVisible();
    if (isFieldStartDateVisible) {
      this.dateFieldStartDateAPI.setValue('');
      this.dateFieldStartDateAPI.setEnabled(!check);
    }

    if (isFieldStartMonthVisible) {
      this.inputStartMonthAPI.setValue('');
      this.inputStartMonthAPI.setEnabled(!check);
    }

    if (isFieldStartYearVisible) {
      this.inputStartYearAPI.setValue('');
      this.inputStartYearAPI.setEnabled(!check);
    }
  }

  private handleDisabledDenies(check: boolean): void {
    const deniesMedicines           = this.context.get('deniesMedicines') as boolean;
    const isFieldStartDateVisible   = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible  = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible   = this.fieldStartYearAPI.isVisible();

    this.locatorMedicineAPI.setEnabled(!check);
    this.inputQtDoseAPI.setEnabled(!check);
    this.inputMedicineAPI.setEnabled(!check);
    this.inputUnregistredMedicineAPI.setEnabled(!check);
    this.autocompleteIntervalAPI.setEnabled(!check);
    this.inputSchedulesAPI.setEnabled(!check);
    this.autocompleteProvenanceAPI.setEnabled(!check);
    this.autocompleteInterruptReasonAPI.setEnabled(!check);
    this.autocompleteDateTypeAPI.setEnabled(!check);
    this.datefieldEndDateAPI.setEnabled(!check);
    this.datefieldLastDateAPI.setEnabled(!check);
    this.textAreaReactionAPI.setEnabled(!check);
    this.checkboxSelfMedAPI.setEnabled(!check);
    this.checkboxUnknownStartAPI.setEnabled(!check);
    this.checkboxUnknownEndAPI.setEnabled(!check);
    this.checkboxUnknownLastDoseAPI.setEnabled(!check);

    if (isFieldStartDateVisible) {
      this.dateFieldStartDateAPI.setEnabled(!check);
    }

    if (isFieldStartMonthVisible) {
      this.inputStartMonthAPI.setEnabled(!check);
    }

    if (isFieldStartYearVisible) {
      this.inputStartYearAPI.setEnabled(!check);
    }

    if (deniesMedicines) {
      return this.handleDeniesEmptyFields();
    }

    return this.handleAvailableFields();
  }

  private handleDeniesEmptyFields(): void {
    const emptyData                 = {} as Record<string, Value>;
    const isFieldStartDateVisible   = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible  = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible   = this.fieldStartYearAPI.isVisible();
    const isUnityMeasureEnabled     = this.autocompleteUnityAPI.isEnabled();
    const isAdmChannelEnabled       = this.autocompleteChannelAdmAPI.isEnabled();

    this.autocompleteChannelAdmAPI.setValue(undefined);
    this.autocompleteDateTypeAPI.setValue(undefined);
    this.autocompleteInterruptReasonAPI.setValue(undefined);
    this.autocompleteIntervalAPI.setValue(undefined);
    this.autocompleteProvenanceAPI.setValue(undefined);

    this.inputQtDoseAPI.setValue('');
    this.inputMedicineAPI.setValue('');
    this.inputSchedulesAPI.setValue('');
    this.inputUnregistredMedicineAPI.setValue('');

    this.datefieldEndDateAPI.setValue('');
    this.datefieldLastDateAPI.setValue('');

    this.checkboxSelfMedAPI.setValue(false);
    this.checkboxUnknownStartAPI.setValue(false);
    this.checkboxUnknownEndAPI.setValue(false);
    this.checkboxUnknownLastDoseAPI.setValue(false);

    this.textAreaReactionAPI.setValue('');

    this.context.set({ medicine: emptyData, medicineCode: null })
    this.handleDisabledField();

    if (isFieldStartDateVisible) {
      this.dateFieldStartDateAPI.setValue('');
    }

    if (isFieldStartMonthVisible) {
      this.inputStartMonthAPI.setValue('');
    }

    if (isFieldStartYearVisible) {
      this.inputStartYearAPI.setValue('');
    }

    if (isUnityMeasureEnabled) {
      this.autocompleteUnityAPI.setValue(undefined);
    }

    if (isAdmChannelEnabled) {
      this.autocompleteChannelAdmAPI.setValue(undefined);
    }
  }

  private handleDisabledField(): void {
    const medicine = this.context.get('medicine');

    HealthHistoryCommons.setEnableFields([
        this.autocompleteUnityAPI,
        this.autocompleteChannelAdmAPI
    ], false);

    if (this.hasMedicine(medicine)) {
      const medicineCode = medicine.id;
      this.context.set({ medicineCode });
      
      this.verifyUnknownInfoMed();
      this.setEmptyFieldByType(this.autocompleteUnityAPI);
      this.setEmptyFieldByType(this.autocompleteChannelAdmAPI);
      this.forceFetchUnityAndChannel();
    } else {
        this.context.set({ medicineCode: null });
        this.setEmptyFieldByType(this.autocompleteUnityAPI);
        this.setEmptyFieldByType(this.autocompleteChannelAdmAPI);
        this.disableMedicines(this.inputUnregistredMedicineAPI.getValue() !== '');
        this.forceFetchUnityAndChannel();
    }

    this.verifyAndSetUnregisteredMedicine(medicine);
  }

  private hasMedicine(medicine: SearchType | unknown): boolean {
    return medicine && (medicine as SearchType).id > 0 ? true : false;
  }

  private verifyUnknownInfoMed() {
    const checkedUnknown = this.fieldUnknownInfoMedAPI.getData();
      this.autocompleteUnityAPI.setEnabled(!checkedUnknown.unknownInfoMedication);
      this.autocompleteChannelAdmAPI.setEnabled(!checkedUnknown.unknownInfoMedication);
  }

  private forceFetchUnityAndChannel() {
    this.fetchUnityMeasureAPI.forceFetch();
    this.fetchChannelAdmAPI.forceFetch();
  }

  private verifyAndSetUnregisteredMedicine(medicine: SearchType | unknown) {
    HealthHistoryCommons.setEnableField(this.inputUnregistredMedicineAPI, !this.hasMedicine(medicine) && !this.isDeniesMedicine());
  }

  private isDeniesMedicine(): boolean {
    return this.context.get('deniesMedicines') as boolean;
  }

  private handleAvailableFields(): void {
    const initialDateType = this.context.get('initialDateType') as Record<string, Value>;
    this.autocompleteDateTypeAPI.setValue(initialDateType);
    this.handleVisibilityDates(initialDateType);
  }

  private handleDisabledUnknown(checked: boolean): void {
    const deniesMedicines = this.context.get('deniesMedicines') as boolean;
    if (!deniesMedicines) {
      this.inputQtDoseAPI.setEnabled(!checked);
      this.autocompleteUnityAPI.setEnabled(!checked);
      this.autocompleteChannelAdmAPI.setEnabled(!checked);
      this.autocompleteIntervalAPI.setEnabled(!checked);
      return;
    }
  }

  private disableMedicines(check: boolean): void {
    if (!this.isDeniesMedicine() && !this.hasMedicine(this.locatorMedicineAPI.getValue())) {
      this.locatorMedicineAPI.setEnabled(!check);
      this.inputMedicineAPI.setEnabled(!check);
      this.autocompleteUnityAPI.setEnabled(check);
      this.autocompleteChannelAdmAPI.setEnabled(true);
    }
  }

  private disablingDateFields(check: boolean, api: DateFieldAPI): void {
    api.setEnabled(!check);
  }

  private setRadioGroupValues(expressionId: number, api: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        api.setData(responseBody);
      }).catch((error) => {
        error?.body && this.showToast(error?.body, ToastLevel.ERROR)
      });
  }

  private handleStepDone(isStepDone: boolean, step: WizardSteps): void {
    if (isStepDone) {
      this.wizardAPI.setStepDone(step, true);
    }
  }

  public mountUnityMeasureAfterFetch(unitiesMeasure: Record<string, Value>[]) {
    this.autocompleteUnityAPI.setData(unitiesMeasure);
    if (unitiesMeasure && unitiesMeasure.length > 0) {
        const medicinesRecord = this.context.get('medicines');
        if (this.isSameMedicine() && medicinesRecord?.unityMeasurement?.valueDomain) {
            const unityFind = unitiesMeasure.find(unity => unity.valueDomain === medicinesRecord.unityMeasurement.valueDomain);
            this.autocompleteUnityAPI.setValue(unityFind);
        } else {
            this.verifyAndSetFirstAutocomplete(this.autocompleteUnityAPI, unitiesMeasure);
        }
    }
  }

  private verifyAndSetFirstAutocomplete(field: any, data: Record<string, Value>[]) {
    if (data.length == 1) {
        field.setValue(data[0]);
    } else {
        this.setEmptyFieldByType(field);
    }
  }

  private setEmptyFieldByType(field: any) {
    if(this.isTypeField(field, AutocompleteAPI)) {
        field.setValue(undefined);
    }
  }

  private isTypeField(field: any, type: any): boolean {
    return field instanceof type;
  }

  public mountChannelAdmAfterFetch(channelsAdm: Record<string, Value>[]) {
    this.autocompleteChannelAdmAPI.setData(channelsAdm);
    if (channelsAdm && channelsAdm.length > 0) {
        const medicinesRecord = this.context.get('medicines');
        if (this.isSameMedicine() && medicinesRecord?.administrationChannel?.valueDomain) {
            const channelFind = channelsAdm.find(channel => channel.valueDomain === medicinesRecord.administrationChannel.valueDomain);
            this.autocompleteChannelAdmAPI.setValue(channelFind);
        } else {
            this.verifyAndSetFirstAutocomplete(this.autocompleteChannelAdmAPI, channelsAdm);
        }
    }
  }

  private isSameMedicine() {
    const medicineCode = this.context.get('medicineCode');
    const medicinesRecord = this.context.get('medicines');
    const medication = medicinesRecord?.medicationDescription
    const unregisteredMedication = this.inputUnregistredMedicineAPI.getValue();

    if (!medicineCode && unregisteredMedication === medicinesRecord?.unregisteredMedication) {
        return true;
    }

    return medicineCode && medication && 
        medicineCode === medication.id;
  }

  private checkUnregistredMed(): void {
    const deniesMedicines         = this.context.get('deniesMedicines') as boolean;
    const medicine                = this.locatorMedicineAPI.getValue() as unknown as Record<string, Value>;
    const medicineId              = this.inputMedicineAPI.getValue();
    const unregisteredMedication  = this.inputUnregistredMedicineAPI.getValue();
    if (unregisteredMedication) {
      this.locatorMedicineAPI.setEnabled(false);
      this.inputMedicineAPI.setEnabled(false);
    }
    if (medicine || medicineId) {
      this.inputUnregistredMedicineAPI.setEnabled(false);
      return;
    }
    if (!deniesMedicines) {
      this.inputUnregistredMedicineAPI.setEnabled(true);
      return;
    }
  }

  private showModalSubmit(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.handleReleaseMedicine(idHealthHistory) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    })
  }

  private handleReleaseMedicine(idHealthHistory: number): void {
    this.formOneAPI.setLoading(true);
    this.formTwoAPI.setLoading(true);
    this.formThreeAPI.setLoading(true);

    HttpService.put(Endpoint.RELEASE_HEALTH_HISTORY_MEDICINE, { sequenceNumberMedicine: idHealthHistory })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneAPI.setLoading(false);
        this.formTwoAPI.setLoading(false);
        this.formThreeAPI.setLoading(false);
      })
  }

  private confirmDelete(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.delete(idHealthHistory); },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private delete(idHealthHistory: number): void {
    const sequenceNumberMedicine = idHealthHistory;
    this.formOneAPI.setLoading(true);
    this.formTwoAPI.setLoading(true);
    this.formThreeAPI.setLoading(true);
    HttpService.delete(Endpoint.DELETE_HEALTH_HISTORY_MEDICINE, {
      sequenceNumberMedicine
    }).then(() => {
      applicationContext.popNavigationItem();
    }).catch((err) => {
      err?.body && this.showToast(err?.body, ToastLevel.ERROR);
    }).finally(() => {
      this.formOneAPI.setLoading(false);
      this.formTwoAPI.setLoading(false);
      this.formThreeAPI.setLoading(false);
    })
  }

  public handleBlurUnregisterMedicine(): void {
    const unregisteredMedicineValue = this.inputUnregistredMedicineAPI.getValue();
    if (unregisteredMedicineValue) {
        this.context.set({medicineDescription: unregisteredMedicineValue});
		this.forceFetchUnityAndChannel();
    } else {
        this.context.set({medicineDescription: null});
    }
  }

  private handleCreateMedicineInUse(body: Record<string, Value>): void {
    this.formOneAPI.setLoading(true);
    this.formTwoAPI.setLoading(true);
    this.formThreeAPI.setLoading(true);

    HttpService.post(Endpoint.POST_HEALTH_HISTORY_MEDICINE, undefined, {body})
      .then((res) => {
        const { idHealthHistory } = res.body as HealthHistoryMedicineResponse;
        this.showModalSubmit(idHealthHistory as number);
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneAPI.setLoading(false);
        this.formTwoAPI.setLoading(false);
        this.formThreeAPI.setLoading(false);
      })
  }
  
  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-medicines-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}